import { useState, useRef, useEffect } from 'react';
import { api, ApiFailure, type Narrative } from '../api';
import { Notice } from './Panels';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  loading?: boolean;
  /** Provenance of the answer, carried per message like NarrativePanel does. */
  narration?: Pick<Narrative, 'degraded' | 'unverified' | 'provider' | 'model'>;
}

/** What the welcome line can say truthfully about the data being discussed. */
export interface ChatContext {
  name: string;
  rowCount: number;
  /** Null where the caller knows the dataset only by reference. */
  columnCount: number | null;
}

const SUGGESTED_QUESTIONS = [
  'What are the top 3 findings in this data?',
  'Summarise the key trends',
  'What anomalies stand out?',
  'What would you recommend based on this data?',
  'Explain the data quality issues',
  'What is the overall story this data tells?',
];

/**
 * AI Chat With Your Data.
 *
 * A conversational interface over the question-answering mode of
 * `POST /api/reports/:id/narrate`: the server narrates from the saved report's
 * scorecards, insights and quality figures, and deletes any sentence quoting a
 * number the analysis never produced. The per-message provenance (degraded,
 * unverified) is rendered exactly as the narrative panel renders it, so a chat
 * answer carries the same warnings as a report summary.
 */
export function AiChat({
  reportId,
  context,
  embedded = false,
}: {
  reportId: string | null;
  context: ChatContext;
  /** True when the chat sits inside another page rather than filling one. */
  embedded?: boolean;
}) {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content:
        context.columnCount === null
          ? `I've analysed "${context.name}" — ${context.rowCount.toLocaleString()} rows. Ask me anything about the data: trends, anomalies, recommendations, or specific columns.`
          : `I've analysed "${context.name}" — ${context.rowCount.toLocaleString()} rows, ${context.columnCount} columns. Ask me anything about the data: trends, anomalies, recommendations, or specific columns.`,
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  async function ask(question: string): Promise<void> {
    if (!question.trim() || reportId === null) return;

    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: question.trim(),
      timestamp: new Date(),
    };

    const loadingMsg: ChatMessage = {
      id: `loading-${Date.now()}`,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      loading: true,
    };

    setMessages((prev) => [...prev, userMsg, loadingMsg]);
    setInput('');
    setBusy(true);
    setFailure(null);

    try {
      const { narration } = await api.narrate(reportId, { question: question.trim() });

      const responseText = [
        narration.summary,
        narration.highlights.length > 0 ? `Key points:\n${narration.highlights.map((h) => `• ${h}`).join('\n')}` : '',
        narration.recommendations.length > 0 ? `Recommendations:\n${narration.recommendations.map((r) => `• ${r}`).join('\n')}` : '',
      ].filter(Boolean).join('\n');

      setMessages((prev) =>
        prev.map((m) =>
          m.id === loadingMsg.id
            ? {
                ...m,
                content: responseText,
                loading: false,
                narration: {
                  degraded: narration.degraded,
                  unverified: narration.unverified,
                  provider: narration.provider,
                  model: narration.model,
                },
              }
            : m,
        ),
      );
    } catch (error) {
      // A 429 lands here with the server's remedy ("summaries are rate limited,
      // wait a few minutes") — surfaced verbatim rather than paraphrased.
      setFailure(error instanceof ApiFailure ? error : new ApiFailure('Could not get an answer.', 'Try again.', 0));
      setMessages((prev) => prev.filter((m) => m.id !== loadingMsg.id));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className={embedded ? undefined : 'page'}
      style={{ display: 'flex', flexDirection: 'column', height: embedded ? 'auto' : 'calc(100vh - 120px)', maxHeight: embedded ? 560 : 800 }}
    >
      <header className="intro" style={{ flexShrink: 0 }}>
        <h2>💬 Ask Your Data</h2>
        <p>
          Ask questions in plain English. Every answer is traced back to computed evidence — nothing is
          invented.
          {embedded && ' Answers draw on the saved analysis of the full dataset; the filters on this page do not change them.'}
        </p>
      </header>

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}

      {reportId === null && (
        <div className="card empty" style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <h3>No analysis to chat with</h3>
          <p>Upload a dataset first. Once the analysis is complete, you can ask questions about it here.</p>
        </div>
      )}

      {reportId !== null && (
        <>
          {/* Message area */}
          <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '16px 0', display: 'flex', flexDirection: 'column', gap: 12, minHeight: 160 }}>
            {messages.map((msg) => (
              <div key={msg.id} style={{
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              }}>
                <div style={{
                  maxWidth: '75%',
                  padding: '12px 16px',
                  borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  background: msg.role === 'user' ? 'var(--series-1)' : 'var(--surface)',
                  color: msg.role === 'user' ? '#fff' : 'var(--ink)',
                  border: msg.role === 'assistant' ? '1px solid var(--border)' : 'none',
                  fontSize: 14,
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap',
                }}>
                  {msg.loading ? (
                    <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <span className="spinner" /> Thinking…
                    </span>
                  ) : (
                    <>
                      {msg.content}
                      {msg.narration !== undefined && (
                        <Provenance narration={msg.narration} />
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Suggested questions */}
          {messages.length <= 2 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '8px 0', flexShrink: 0 }}>
              {SUGGESTED_QUESTIONS.map((q) => (
                <button
                  key={q}
                  className="ghost tiny"
                  onClick={() => void ask(q)}
                  disabled={busy}
                  style={{ fontSize: 12 }}
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input area */}
          <div style={{
            display: 'flex', gap: 8, padding: '12px 0', flexShrink: 0,
            borderTop: '1px solid var(--border)',
          }}>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void ask(input); } }}
              placeholder="Ask anything about your data…"
              disabled={busy}
              style={{
                flex: 1, padding: '10px 14px', borderRadius: 10,
                border: '1px solid var(--border)', fontSize: 14,
                background: 'var(--surface)', color: 'var(--ink)',
                outline: 'none',
              }}
            />
            <button
              className="primary"
              onClick={() => void ask(input)}
              disabled={busy || input.trim() === ''}
              style={{ borderRadius: 10, padding: '10px 20px' }}
            >
              {busy ? <span className="spinner" /> : 'Ask'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Who wrote an answer and what was removed from it — the same two warnings the
 * narrative panel shows, attached to the message they describe.
 */
function Provenance({ narration }: { narration: NonNullable<ChatMessage['narration']> }) {
  return (
    <div style={{ marginTop: 8, fontSize: 12 }}>
      <p className="muted" style={{ margin: 0, whiteSpace: 'normal' }}>
        {narration.degraded
          ? 'Written offline — the configured AI provider was unavailable. The figures are unaffected.'
          : narration.provider === 'deterministic'
            ? 'Composed directly from the computed findings. No AI provider is configured.'
            : `Written by ${narration.provider}${narration.model === null ? '' : ` (${narration.model})`}. Figures were computed by the analytics core, not by the model.`}
      </p>
      {narration.unverified.length > 0 && (
        <div style={{ marginTop: 8, whiteSpace: 'normal' }}>
          <Notice
            message={`${narration.unverified.length} figure(s) removed from this answer.`}
            remedy={`The model quoted ${narration.unverified.join(', ')}, which do not appear in the analysis. The sentences containing them were dropped.`}
          />
        </div>
      )}
    </div>
  );
}
